import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { passwordSchema } from '@system-atlas/shared';
import { useAuth } from '../auth/AuthContext';
import { ApiError } from '../lib/api';
import { Alert, Button, Card, Label, TextInput } from '../components/ui';

export default function ChangePasswordPage() {
  const { t } = useTranslation();
  const { user, changePassword } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirm) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }
    const policy = passwordSchema.safeParse(newPassword);
    if (!policy.success) {
      setError(policy.error.issues.map((i) => i.message).join(' '));
      return;
    }

    setBusy(true);
    try {
      await changePassword(currentPassword, newPassword);
      navigate('/');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mx-auto flex max-w-md flex-col gap-6 px-6 py-16">
      <div className="text-center">
        <h1 className="text-2xl font-bold tracking-tight">{t('auth.changePasswordTitle')}</h1>
        {user?.mustChangePassword && (
          <p className="mt-1 text-sm text-amber-700 dark:text-amber-300">{t('auth.mustChangeHint')}</p>
        )}
      </div>
      <Card>
        <form onSubmit={submit} className="flex flex-col gap-4">
          {error && <Alert kind="error">{error}</Alert>}
          <div>
            <Label htmlFor="current">{t('auth.currentPassword')}</Label>
            <TextInput
              id="current"
              type="password"
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="new">{t('auth.newPassword')}</Label>
            <TextInput
              id="new"
              type="password"
              autoComplete="new-password"
              required
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{t('auth.passwordPolicy')}</p>
          </div>
          <div>
            <Label htmlFor="confirm">{t('auth.confirmPassword')}</Label>
            <TextInput
              id="confirm"
              type="password"
              autoComplete="new-password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={busy}>
            {busy ? t('common.pleaseWait') : t('auth.saveNewPassword')}
          </Button>
        </form>
      </Card>
    </div>
  );
}
